/**
 * @class StorageService
 * A class to abstract basic storage operations using a storage backend like localStorage.
 */
class StorageService {
    /**
     * @constructor
     * @param {Storage} [storageBackend=localStorage] - The storage backend to use (localStorage by default).
     */
    constructor(storageBackend = localStorage) {
        /**
         * @type {Storage}
         */
        this.storage = storageBackend;
    }

    /**
     * Sets a value in storage.
     * @param {string} key - The key to set in storage.
     * @param {string} value - The value to associate with the key.
     * @returns {void}
     */
    setItem(key, value) {
        try {
            this.storage.setItem(key, value);
        } catch (error) {
            console.error(`Error setting ${key} in storage:`, error);
        }
    }

    /**
     * Gets a value from storage.
     * @param {string} key - The key to retrieve from storage.
     * @returns {string|null} - The value associated with the key, or null if the key doesn't exist or there's an error.
     */
    getItem(key) {
        try {
            return this.storage.getItem(key);
        } catch (error) {
            console.error(`Error getting ${key} from storage:`, error);
            return null;
        }
    }

    /**
     * Removes a value from storage.
     * @param {string} key - The key to remove from storage.
     * @returns {void}
     */
    removeItem(key) {
        try {
            this.storage.removeItem(key);
        } catch (error) {
            console.error(`Error removing ${key} from storage:`, error);
        }
    }

    /**
     * Clears all keys from the storage.
     * @returns {void}
     */
    clear() {
        try {
            this.storage.clear();
        } catch (error) {
            console.error('Error clearing storage:', error);
        }
    }
}

/**
 * @class AppStorage
 * A class that extends StorageService to manage application-specific storage operations like tokens and company IDs.
 */
class AppStorage extends StorageService {
    /**
     * Sets the token in storage.
     * @param {string} token - The authentication token.
     * @returns {void}
     */
    setToken(token) {
        this.setItem("token", token);
    }

    /**
     * Gets the token from storage.
     * @returns {string|null} - The token from storage, or null if not found.
     */
    getToken() {
        return this.getItem("token");
    }

    /**
     * Removes the token from storage.
     * @returns {void}
     */
    removeToken() {
        this.removeItem("token");
    }

    /**
     * Sets the company ID in storage.
     * @param {string} companyId - The company ID.
     * @returns {void}
     */
    setCompanyID(companyId) {
        this.setItem("companyId", companyId);
    }

    /**
     * Gets the company ID from storage.
     * @returns {string|null} - The company ID from storage, or null if not found.
     */
    getCompanyID() {
        return this.getItem("companyId");
    }

    /**
     * Removes the company ID from storage.
     * @returns {void}
     */
    removeCompanyID() {
        this.removeItem("companyId");
    }
}

/**
 * The default instance of AppStorage that uses localStorage as the backend.
 * @type {AppStorage}
 */
const defaultStorage = new AppStorage();

export default defaultStorage;
